import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Modal, Form, Input, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useClients, useCreateClient } from "../../queries/clients";
import { useProjects } from "../../queries/projects";
import { ClientProps, ProjectProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { StatusTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import { NoAccess } from "../../components/ui/NoAccess";
import AuthContext from "../../context/AuthContext";

export const ClientListPage = () => {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isAdmin = userData?.role === "admin";
  const { data: clients, isLoading, isError, error } = useClients();
  const { data: projects } = useProjects();
  const createClient = useCreateClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [form] = Form.useForm();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (clients || []).filter((c: ClientProps) => (q ? c.name.toLowerCase().includes(q) : true));
  }, [clients, query]);

  const projectCount = (clientId: string) =>
    (projects || []).filter((p: ProjectProps) => p.clientId === clientId).length;

  const handleCreate = async () => {
    const values = await form.validateFields();
    try {
      await createClient.mutateAsync(values);
      setIsModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the client");
    }
  };

  if (userData?.role === "final_user") return <NoAccess what="the client directory" />;

  return (
    <PageShell
      kind="list"
      kicker="Accounts"
      title="Clients"
      actions={
        isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
            New client
          </Button>
        )
      }
    >
      <Toolbar count={rows.length} unit="clients">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--q-text-faint)" }} />}
          placeholder="Search clients"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<ClientProps>
          rowKey="_id"
          size="small"
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25, size: "small" } : false}
          onRow={(record) => ({
            onClick: () => navigate(`/clients/${record._id}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <EmptyState
                title="No clients yet"
                description="Add the first client to open projects, boards and billing against it."
                actions={isAdmin && <Button type="primary" onClick={() => setIsModalOpen(true)}>New client</Button>}
              />
            ),
          }}
          columns={[
            { title: "Name", dataIndex: "name", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
            {
              title: "Projects", dataIndex: "_id", width: 100, align: "right",
              render: (id: string) => {
                const n = projectCount(id);
                return <span className="q-num" style={{ color: n ? undefined : "var(--q-text-faint)" }}>{n}</span>;
              },
            },
            {
              title: "Email", dataIndex: "email",
              render: (v?: string) => v || <span style={{ color: "var(--q-text-faint)" }}>—</span>,
            },
            {
              title: "Phone", dataIndex: "phone", width: 160,
              render: (v?: string) => v
                ? <span className="q-num">{v}</span>
                : <span style={{ color: "var(--q-text-faint)" }}>—</span>,
            },
            {
              title: "Status", dataIndex: "active", width: 120,
              render: (active: boolean) => (
                <StatusTag label={active ? "Active" : "Inactive"} tone={active ? "ok" : "neutral"} dot />
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title="New client"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleCreate}
        okText="Create client"
        confirmLoading={createClient.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="email" label="Email"><Input /></Form.Item>
          <Form.Item name="phone" label="Phone"><Input /></Form.Item>
          <Form.Item name="billingAddress" label="Billing address"><Input /></Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
